import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import ImpersonationBanner from './components/ImpersonationBanner';
import { Toaster } from './components/ui/sonner';
import DashboardLayout from './components/DashboardLayout';
import ProjectLayout from './components/ProjectLayout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import Dashboard from './pages/Dashboard';
import ScriptUploadPage from './pages/ScriptUploadPage';
import MasterBreakdownPage from './pages/MasterBreakdownPage';
import DepartmentDashboard from './pages/DepartmentDashboard';
import ProjectUsersPage from './pages/ProjectUsersPage';
import ShareLinksPage from './pages/ShareLinksPage';
import SharedBreakdownView from './pages/SharedBreakdownView';
import ActivityLogPage from './pages/ActivityLogPage';
import SnapshotsPage from './pages/SnapshotsPage';
import BudgetPage from './pages/BudgetPage';
import StripBoardPage from './pages/StripBoardPage';
import ScriptPdfPage from './pages/ScriptPdfPage';
import SchedulePage from './pages/SchedulePage';
import ArtDepartmentPage from './pages/ArtDepartmentPage';
import CostumeDepartmentPage from './pages/CostumeDepartmentPage';
import ProductionPage from './pages/ProductionPage';
import CastingDeptPage from './pages/CastingDeptPage';
import VFXDeptPage from './pages/VFXDeptPage';
import ContinuityBoardPage from './pages/ContinuityBoardPage';
import BeatBoardPage from './pages/BeatBoardPage';
import ProfilePage from './pages/ProfilePage';
import SlatePage from './pages/SlatePage';
import ThemeMarketplace from './pages/ThemeMarketplace';
import CallSheetListPage from './pages/CallSheetListPage';
import CallSheetPage from './pages/CallSheetPage';
import AdminBetaPage from './pages/AdminBetaPage';
import ProjectsPage from './pages/ProjectsPage';
import TokenDebugOverlay from './components/TokenDebugOverlay';

// P5: Ops Console — lazy-loaded subtree (chunk never shipped with main bundle)
const MxConsoleApp = React.lazy(() => import('./mx-console/MxConsoleApp'));

// Intercepts Google OAuth redirects synchronously before the rest of the router
// renders — prevents race conditions with ProtectedRoute's auth check.
function AppRouter() {
  const location = useLocation();
  // P15: Ops Console impersonation token handler.
  // SEC-002.5 (2026-07-07): new flow — `?imp=1` on landing means the server
  // has just set an HttpOnly `imp_bootstrap` cookie. POST /imp-exchange
  // swaps it for the real imp JWT (one-time). Legacy `?impersonation_token=`
  // branch retained for soft-deprecation until we've confirmed no external
  // tools depend on it.
  // sessionStorage (NOT localStorage) so the token dies with the tab.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('impersonation_token');
    const imp = params.get('imp');
    if (tok) {
      sessionStorage.setItem('auth_token', tok);
      sessionStorage.setItem('impersonation_token', tok);
      params.delete('impersonation_token');
      const cleanUrl = window.location.pathname +
        (params.toString() ? `?${params}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    } else if (imp === '1') {
      // Exchange the imp_bootstrap cookie for a real JWT (one-time).
      const apiBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin)
        .replace(/\/api\/?$/, '') + '/api';
      fetch(`${apiBase}/mx-console/imp-exchange`, {
        method: 'POST',
        credentials: 'include',
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.access_token) {
            sessionStorage.setItem('auth_token', data.access_token);
            sessionStorage.setItem('impersonation_token', data.access_token);
          }
        })
        .catch(() => {})
        .finally(() => {
          params.delete('imp');
          const cleanUrl = window.location.pathname +
            (params.toString() ? `?${params}` : '') + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        });
    }
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <>
    <TokenDebugOverlay />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/share/:token" element={<SharedBreakdownView />} />

      {/* Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="themes" element={<ThemeMarketplace />} />
        <Route path="beta-requests" element={<AdminBetaPage />} />
      </Route>

      {/* Projects list — grid-only view under DashboardLayout */}
      <Route path="/projects" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<ProjectsPage />} />
      </Route>

      {/* Script upload – standalone (no sidebar, handles pre-project flow) */}
      <Route path="/projects/:projectId/upload" element={<ProtectedRoute><ScriptUploadPage /></ProtectedRoute>} />

      {/* All project pages – wrapped in ProjectLayout (persistent sidebar) */}
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectLayout /></ProtectedRoute>}>
        <Route path="breakdown" element={<MasterBreakdownPage />} />
        <Route path="script"    element={<ScriptPdfPage />} />

        <Route path="stripboard" element={<StripBoardPage />} />
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="users" element={<ProjectUsersPage />} />
        <Route path="share" element={<ShareLinksPage />} />
        <Route path="activity" element={<ActivityLogPage />} />
        <Route path="versions" element={<SnapshotsPage />} />
        <Route path="budget" element={<BudgetPage />} />
        {/* Department routes */}
        <Route path="dept/art" element={<ArtDepartmentPage />} />
        <Route path="dept/costume" element={<CostumeDepartmentPage />} />
        <Route path="dept/casting" element={<CastingDeptPage />} />
        <Route path="dept/vfx" element={<VFXDeptPage />} />
        <Route path="dept/production" element={<ProductionPage />} />
        <Route path="continuity" element={<ContinuityBoardPage />} />
        <Route path="beatboard"  element={<BeatBoardPage />} />
        <Route path="slate" element={<SlatePage />} />
        <Route path="callsheet" element={<CallSheetListPage />} />
        <Route path="callsheet/:sheetId" element={<CallSheetPage />} />
        {/* Legacy department dashboard */}
        <Route path="department/:department" element={<DepartmentDashboard />} />
      </Route>

      {/* Catch all */}
      <Route path="/mx-console/*" element={
        <React.Suspense fallback={null}>
          <MxConsoleApp />
        </React.Suspense>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <AppRouter />
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'var(--cb-surface)',
              border: '1px solid var(--cb-border)',
              color: 'var(--cb-text)',
            },
          }}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
